<template>
  <div class="commentGrid" :class="{ isFullList }">
    <template v-for="(revision, revisionIndex) in visibleRevisions" :key="`date-${revisionIndex}`">
      <div v-if="isFullList">
        <div class="date">
          {{ getDate(revision.createdAt) }}
        </div>
        <div class="pr-3 lisaui:whitespace-nowrap">
          {{ getUserName(users, revision.changedById) }}
        </div>
      </div>
      <template v-else>
        <div class="date">
          {{ getDate(revision.createdAt) }}
        </div>
        <div class="pr-3 lisaui:whitespace-nowrap">
          | {{ getUserName(users, revision.changedById) }}
        </div>
      </template>
      <div class="breakable mr-3">
        <div
          v-for="(change, index) in visibleChanges(revision)"
          :key="`change-${index}|${revisionIndex}`"
        >
          <span class="text-info mr-1">
            {{ getTypeNameTranslation(change.fieldType, change.fieldId) }}
          </span>
          <span class="revision-change-value">
            <span>[</span>
            <template v-if="hasOldValue(change)">
              <span class="text-muted white-space-pre">{{
                getTypeNameFieldTranslation(change.fieldType, change.oldValue, change.fieldId)
              }}</span>
              <i class="fa fa-arrow-right fa-xs" />
            </template>
            <span class="white-space-pre">{{
              getTypeNameFieldTranslation(change.fieldType, change.newValue, change.fieldId)
            }}</span>
            <span>]</span>
          </span>
        </div>
        <span :class="{ 'lisaui:font-bold': isFullList }" class="white-space-pre">
          {{ revision.comment }}
        </span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue";
import { DateTime } from "luxon";
import { LISA_DATE_SHORT } from "@/language/translation";
import { useTranslation } from "@/language/translation";
import { getUserName, type EABEventActionType, type EABEventType } from "./EventActionHelper";
import {
  SQDCBoardEventStatus,
  SQDCActionRevisionFieldType,
  SQDCEventActionTypeFieldType,
  SQDCEventRevisionFieldType,
  SQDCEventTypeFieldRelation,
} from "@/graphql";
import {
  formatErrorCatalogueRevisionValue,
  useRevisionMetadata,
  type RevisionTypeField,
} from "./EventActionRevisionHelper";
import {
  formatDatapointRevisionValue,
  formatRevisionFieldValue,
  getRevisionFieldLabel,
  type RevisionDisplayFormatters,
} from "./EventActionRevisionDisplayHelper";
import { getBoardNameFromCache } from "@/modules/sqdc/helper/EABConfigHelper";
import { formatSQDCButtonModuleValue } from "@/modules/sqdc/helper/SQDCButtonModuleHelper";
import { formatEnumValue } from "@/modules/sqdc/helper/SQDCEnumValueHelper";
import { formatSQDCRepairWindowEnumValue } from "@/modules/sqdc/helper/SQDCRepairWindowHelper";
import { parseEABFieldRecordValue } from "./EABEventFieldHelper";
import { formatSQDCFiveWTwoHValue } from "./modals/SQDCFiveWTwoHDraft";
const props = defineProps({
  revisions: {
    type: Array as PropType<
      EABEventActionType["SQDCActionRevisions"] | EABEventType["SQDCEventRevisions"]
    >,
    required: true,
  },
  isFullList: {
    type: Boolean,
    default: false,
  },
  eventActionTypeFields: {
    type: Array as PropType<RevisionTypeField[]>,
    required: false,
    default: () => [],
  },
  boardEventConnections: {
    type: Array as PropType<EABEventType["BoardEventConnection"]>,
    required: false,
    default: () => [],
  },
});

const sortedRevisions = computed(() =>
  props.revisions.toSorted((a, b) => (a.createdAt > b.createdAt ? 1 : -1)),
);
const visibleRevisions = computed(() =>
  sortedRevisions.value.filter(
    (revision) => props.isFullList || rawVisibleChanges(revision).length > 0 || revision.comment,
  ),
);
const eventActionTypeFields = computed(() => props.eventActionTypeFields);
const revisionMetadata = useRevisionMetadata(
  sortedRevisions,
  eventActionTypeFields,
  computed(() => props.revisions.length > 0),
);
const users = computed(() => revisionMetadata.users.value);

const { locale, te } = useTranslation();
function getDate(dateString?: string) {
  if (!dateString) {
    return t("global.na");
  }
  const date = DateTime.fromISO(dateString);
  return props.isFullList
    ? date.setLocale(locale.value).toLocaleString({
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : date.setLocale(locale.value).toLocaleString(LISA_DATE_SHORT);
}
const { t } = useTranslation();

const revisionDisplayFormatters: RevisionDisplayFormatters = {
  t: (key) => String(t(key)),
  formatCustomFieldLabel: getCustomFieldLabel,
  formatCustomFieldValue,
  formatDate: getDate,
  formatDatapoints: (value) => formatDatapointRevisionValue(value, getDate, formatPlainValue),
  formatFlags: formatFlagValues,
  formatMove: formatBoardMoveValue,
  formatPlain: formatPlainValue,
  formatStatus: formatStatusValue,
  formatUser: formatUserValue,
  formatValueIds: (value) =>
    formatValueIds(value, (id) => revisionMetadata.linkedEventLabels.value.get(id) ?? String(id)),
};

function rawVisibleChanges(revision: (typeof props.revisions)[number]) {
  const changes = revision.changes ?? [];
  return props.isFullList ? changes : changes.filter((change) => change.includeInLast !== false);
}

function visibleChanges(revision: (typeof props.revisions)[number]) {
  return rawVisibleChanges(revision).filter((change) => hasVisibleChangeValue(change));
}

function getTypeNameTranslation(
  fieldType: SQDCActionRevisionFieldType | SQDCEventRevisionFieldType,
  fieldId?: number,
) {
  return getRevisionFieldLabel(fieldType, fieldId, revisionDisplayFormatters);
}

function getTypeNameFieldTranslation(
  fieldType: SQDCActionRevisionFieldType | SQDCEventRevisionFieldType,
  fieldValue: unknown,
  fieldId: number | undefined,
) {
  return formatRevisionFieldValue(fieldType, fieldValue, fieldId, revisionDisplayFormatters);
}

function hasOldValue(change: NonNullable<(typeof props.revisions)[number]["changes"]>[number]) {
  return getTypeNameFieldTranslation(change.fieldType, change.oldValue, change.fieldId) !== "";
}

function hasVisibleChangeValue(
  change: NonNullable<(typeof props.revisions)[number]["changes"]>[number],
) {
  return (
    hasOldValue(change) ||
    getTypeNameFieldTranslation(change.fieldType, change.newValue, change.fieldId) !== ""
  );
}

function getCustomFieldLabel(fieldId?: number) {
  const field = fieldId == null ? undefined : revisionMetadata.customFieldMap.value.get(fieldId);
  if (field == null) {
    return `Custom Field ${fieldId}`;
  }
  if (te(`dynamic.${field.nameId}`)) {
    return t(`dynamic.${field.nameId}`);
  }
  return getCustomFieldFallbackLabel(field);
}

function getCustomFieldFallbackLabel(field: RevisionTypeField) {
  if (field.type === SQDCEventActionTypeFieldType.RELATION) {
    const relationKey = `modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.relation.${field.relation}`;
    if (te(relationKey)) {
      return t(relationKey);
    }
  }
  return getCustomFieldTypeLabel(field.type);
}

function getCustomFieldTypeLabel(type: SQDCEventActionTypeFieldType) {
  const key = `modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.${type}`;
  return te(key) ? t(key) : formatPlainValue(type);
}

function formatCustomFieldValue(fieldId: number | undefined, value: unknown) {
  const field = fieldId == null ? undefined : revisionMetadata.customFieldMap.value.get(fieldId);
  if (field == null) {
    return formatPlainValue(value);
  }
  if (
    field.type === SQDCEventActionTypeFieldType.DATE ||
    field.type === SQDCEventActionTypeFieldType.DATERANGE
  ) {
    return (Array.isArray(value) ? value : [value])
      .map((entry) => (typeof entry === "string" ? getDate(entry) : formatPlainValue(entry)))
      .join(" - ");
  }
  if (field.type === SQDCEventActionTypeFieldType.BOOLEAN) {
    return value ? t("global.yes") : t("global.no");
  }
  if (field.type === SQDCEventActionTypeFieldType.ENUM) {
    return formatEnumValue(field.EnumValues, String(value), locale.value, t);
  }
  if (field.type === SQDCEventActionTypeFieldType.ENUM_NUMBER) {
    return formatEnumNumberValue(field, value);
  }
  if (field.type === SQDCEventActionTypeFieldType.REPAIR_WINDOW) {
    return formatRepairWindowValue(field, value);
  }
  if (field.type === SQDCEventActionTypeFieldType.OUTAGE) {
    return formatOutageValue(value);
  }
  if (field.type === SQDCEventActionTypeFieldType.PERCENT_SHARING) {
    return formatPercentSharingValue(value);
  }
  if (
    field.type === SQDCEventActionTypeFieldType.VETO ||
    field.type === SQDCEventActionTypeFieldType.RECOMMENDATION
  ) {
    return String(formatSQDCButtonModuleValue(field.type, value, t));
  }
  if (field.type === SQDCEventActionTypeFieldType.PICTURE) {
    return formatPictureValue(value);
  }
  if (field.type === SQDCEventActionTypeFieldType.ISHIKAWA) {
    return formatIshikawaValue(value);
  }
  if (field.type === SQDCEventActionTypeFieldType.FIVE_WHY) {
    return formatFiveWhyValue(value);
  }
  if (field.type === SQDCEventActionTypeFieldType.FIVE_W_TWO_H) {
    return formatSQDCFiveWTwoHValue(value, (key) =>
      String(t(`modules.sqdc.components.eventActionBoard.modals.SQDCFiveWTwoHField.${key}`)),
    );
  }
  if (field.type === SQDCEventActionTypeFieldType.COMPLETION) {
    return formatCompletionValue(value);
  }
  if (field.type === SQDCEventActionTypeFieldType.ACTIONS) {
    return formatActionsValue(value);
  }
  if (field.type === SQDCEventActionTypeFieldType.RELATION) {
    return formatRelationValue(field.relation, value);
  }
  return formatPlainValue(value);
}

function formatPictureValue(value: unknown) {
  const values = Array.isArray(value) ? value : [value];
  return String(values.filter((entry) => entry != null && entry !== "").length);
}

function formatEnumNumberValue(field: RevisionTypeField, value: unknown) {
  const enumNumber = parseEABFieldRecordValue(value);
  if (enumNumber == null) {
    return formatPlainValue(value);
  }
  const enumValue =
    typeof enumNumber.enumValue === "string" && enumNumber.enumValue.trim() !== ""
      ? formatEnumValue(field.EnumValues, enumNumber.enumValue, locale.value, t)
      : "";
  const numberValue =
    enumNumber.numberValue == null || enumNumber.numberValue === ""
      ? ""
      : formatPlainValue(enumNumber.numberValue);
  return [enumValue, numberValue].filter((entry) => entry !== "").join(" / ");
}

function formatRepairWindowValue(field: RevisionTypeField, value: unknown) {
  const repairWindow = parseEABFieldRecordValue(value);
  if (repairWindow == null) {
    return formatPlainValue(value);
  }
  const enumValue =
    typeof repairWindow.enumValue === "string" && repairWindow.enumValue.trim() !== ""
      ? formatSQDCRepairWindowEnumValue(field.EnumValues, repairWindow.enumValue, locale.value, t)
      : "";
  const dateValue =
    typeof repairWindow.dateValue === "string" && repairWindow.dateValue.trim() !== ""
      ? getDate(repairWindow.dateValue)
      : "";
  return [enumValue, dateValue].filter((entry) => entry !== "").join(" / ");
}

function formatIshikawaValue(value: unknown) {
  const ishikawa = parseEABFieldRecordValue(value);
  if (ishikawa == null) {
    return formatPlainValue(value);
  }
  const name = typeof ishikawa.name === "string" ? ishikawa.name : "";
  const reasons = Array.isArray(ishikawa.reasons) ? ishikawa.reasons.length : 0;
  return reasons > 0
    ? `${name} (${t("modules.sqdc.components.eventActionBoard.modals.SQDCIshikawaField.causeCount", { count: reasons })})`
    : name;
}

function formatFiveWhyValue(value: unknown) {
  const fiveWhy = parseEABFieldRecordValue(value);
  const items = fiveWhy == null ? (Array.isArray(value) ? value : []) : fiveWhy.items;
  if (!Array.isArray(items)) {
    return formatPlainValue(value);
  }
  return items
    .map((entry) => {
      const item = parseEABFieldRecordValue(entry);
      if (item == null) {
        return formatPlainValue(entry);
      }
      return [item.cause, item.why1, item.why2, item.why3, item.why4, item.why5]
        .filter((part): part is string => typeof part === "string" && part.trim() !== "")
        .join(" > ");
    })
    .filter((entry) => entry !== "")
    .join("\n");
}

function formatCompletionValue(value: unknown) {
  const completion = parseEABFieldRecordValue(value);
  if (completion == null) {
    return formatPlainValue(value);
  }
  const checks = [
    completion.presentedInSteeringCommittee ?? completion.steeringCommitteePresented,
    completion.reportedCompleteByTeamLead ?? completion.teamLeadCompleted,
    completion.closedByManager ?? completion.managerClosed,
  ].filter(Boolean).length;
  const hashtags = Array.isArray(completion.hashtags)
    ? completion.hashtags.filter((entry): entry is string => typeof entry === "string")
    : [];
  const checkLabel = t(
    "modules.sqdc.components.eventActionBoard.modals.SQDCRevisionModal.completionChecks",
    { count: checks, total: 3 },
  );
  return hashtags.length > 0 ? `${checkLabel}: ${hashtags.join(", ")}` : checkLabel;
}

function formatActionsValue(value: unknown) {
  const values = Array.isArray(value) ? value : [value];
  const count = values.filter((entry) => entry != null && entry !== "").length;
  return count === 0 ? "" : String(count);
}

function formatOutageValue(value: unknown) {
  if (value == null) {
    return "";
  }
  const outageValue = parseEABFieldRecordValue(value);
  if (outageValue == null || !("hasOutage" in outageValue)) {
    return formatPlainValue(value);
  }

  if (!outageValue.hasOutage) {
    return t("modules.sqdc.components.eventActionBoard.modals.SQDCOutageField.noOutage");
  }

  const dates = [outageValue.startDate, outageValue.endDate]
    .filter((entry): entry is string => typeof entry === "string" && entry.trim() !== "")
    .map((entry) => DateTime.fromISO(entry))
    .filter((entry) => entry.isValid)
    .map((entry) => entry.setLocale(locale.value).toLocaleString(DateTime.DATETIME_SHORT));
  const outage = t("modules.sqdc.components.eventActionBoard.modals.SQDCOutageField.outage");
  return dates.length > 0 ? `${outage}: ${dates.join(" - ")}` : outage;
}

function formatPercentSharingValue(value: unknown) {
  if (value == null) {
    return "";
  }
  const percentSharingValue = parseEABFieldRecordValue(value);
  if (percentSharingValue == null || !("participants" in percentSharingValue)) {
    return formatPlainValue(value);
  }

  const participants = percentSharingValue.participants;
  if (!Array.isArray(participants)) {
    return "";
  }

  return participants
    .map((participant) => {
      const participantValue = parseEABFieldRecordValue(participant);
      if (participantValue == null) {
        return formatPlainValue(participant);
      }
      const userId = toNumber(participantValue.userId);
      const percentage = toNumber(participantValue.percentage);
      const user =
        userId == null
          ? formatPlainValue(participantValue.userId)
          : getUserName(users.value, userId);
      return percentage == null ? user : `${user}: ${percentage}%`;
    })
    .filter((entry) => entry !== "")
    .join(", ");
}

function formatRelationValue(relation: SQDCEventTypeFieldRelation, value: unknown) {
  if (relation === SQDCEventTypeFieldRelation.ERROR_CATALOGUE) {
    return formatErrorCatalogueRevisionValue(
      value,
      revisionMetadata.productFamilies.value,
      revisionMetadata.errorCatalogues.value,
      locale.value,
      t,
      formatPlainValue,
    );
  }
  return formatValueIds(value, (id) => {
    switch (relation) {
      case SQDCEventTypeFieldRelation.USER:
        return getUserName(users.value, id);
      case SQDCEventTypeFieldRelation.COST_CENTER: {
        const costCenter = revisionMetadata.costCenters.value.find((entry) => entry.id === id);
        return costCenter == null ? `Cost Center ${id}` : `${costCenter.path} ${costCenter.name}`;
      }
      case SQDCEventTypeFieldRelation.TECHNICAL_PLACE: {
        const technicalPlace = revisionMetadata.technicalPlaces.value.find(
          (entry) => entry.id === id,
        );
        return technicalPlace == null
          ? `Technical Place ${id}`
          : `${technicalPlace.path} ${technicalPlace.name}`;
      }
      case SQDCEventTypeFieldRelation.IDEA_CATEGORY: {
        const category = revisionMetadata.ideaCategories.value.find((entry) => entry.id === id);
        return category == null ? `Idea Category ${id}` : category.name;
      }
      default:
        return String(id);
    }
  });
}

function formatUserValue(value: unknown) {
  const id = toNumber(value);
  return id == null ? formatPlainValue(value) : getUserName(users.value, id);
}

function formatBoardMoveValue(value: unknown) {
  const connection = findBoardConnection(value);
  if (connection == null) {
    const status = normalizeBoardMoveValue(value).status;
    if (status != null) {
      return formatBoardStatus(status);
    }
    return formatPlainValue(value);
  }
  const boardName = getBoardNameFromCache(connection.boardId) ?? `Board ${connection.boardId}`;
  const reason = connection.reason?.trim()
    ? ` (${t("components.taskboard.moveIssueModal.reason")}: ${connection.reason})`
    : "";
  return `${formatBoardStatus(connection.status)}: ${boardName}${reason}`;
}

function findBoardConnection(value: unknown) {
  const normalized = normalizeBoardMoveValue(value);
  if (normalized.boardId != null) {
    return (
      props.boardEventConnections.find(
        (connection) =>
          connection.boardId === normalized.boardId &&
          (normalized.status == null || connection.status === normalized.status),
      ) ??
      (normalized.status == null
        ? undefined
        : {
            boardId: normalized.boardId,
            status: normalized.status,
            reason: normalized.reason ?? "",
          })
    );
  }
  if (normalized.status != null) {
    return props.boardEventConnections.find(
      (connection) => connection.status === normalized.status,
    );
  }
  return undefined;
}

function normalizeBoardMoveValue(value: unknown): {
  boardId?: number;
  status?: SQDCBoardEventStatus;
  reason?: string;
} {
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return {
      boardId: toNumber(record.boardId),
      status: toBoardStatus(record.status),
      reason: typeof record.reason === "string" ? record.reason : undefined,
    };
  }
  const numericValue = toNumber(value);
  if (numericValue != null) {
    return { boardId: numericValue };
  }
  return { status: toBoardStatus(value) };
}

function toBoardStatus(value: unknown) {
  return Object.values(SQDCBoardEventStatus).find((status) => status === value);
}

function formatBoardStatus(status: SQDCBoardEventStatus) {
  switch (status) {
    case SQDCBoardEventStatus.ESCALATED:
      return t("components.event-action.issue.BoardOriginArrow.escalated");
    case SQDCBoardEventStatus.DELEGATED:
      return t("components.event-action.issue.BoardOriginArrow.delegated");
    case SQDCBoardEventStatus.CREATED:
      return t("components.event-action.issue.BoardOriginArrow.created");
    default:
      return formatPlainValue(status);
  }
}

function formatFlagValues(value: unknown) {
  return formatValueIds(value, (id) => {
    const flag = revisionMetadata.flags.value.find((entry) => entry.id === id);
    if (flag == null) {
      return `Flag ${id}`;
    }
    if (flag.abbreviationId && te(`dynamic.${flag.abbreviationId}`)) {
      return t(`dynamic.${flag.abbreviationId}`);
    }
    if (flag.displayNameId && te(`dynamic.${flag.displayNameId}`)) {
      return t(`dynamic.${flag.displayNameId}`);
    }
    return flag.name ?? `Flag ${id}`;
  });
}

function formatValueIds(value: unknown, formatter: (id: number) => string) {
  const values = Array.isArray(value) ? value : [value];
  return values
    .map((entry) => {
      const id = toNumber(entry);
      return id == null ? formatPlainValue(entry) : formatter(id);
    })
    .join(", ");
}

function toNumber(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function formatStatusValue(value: unknown) {
  switch (value) {
    case "PLAN":
      return "Plan";
    case "DO":
      return "Do";
    case "CHECK":
      return "Check";
    case "ACT":
      return "Act";
    default:
      return formatPlainValue(value);
  }
}

function formatPlainValue(value: unknown) {
  if (value == null) {
    return "";
  }
  return Array.isArray(value) || typeof value === "object" ? JSON.stringify(value) : String(value);
}
</script>

<style lang="scss" scoped>
.commentGrid {
  display: grid;
  gap: 4px;
  grid-template-columns: min-content max-content auto;
  align-items: baseline;
  &.isFullList {
    max-height: 180px;
    overflow-y: auto;
    grid-template-columns: max-content auto;
  }
  .breakable {
    min-width: 0;
    min-height: 0;
    word-wrap: break-word;
  }
  .date {
    white-space: nowrap;
  }
}

.revision-change-value {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
}
</style>
